// Entrypoint for the React SPA. This wires up global providers, the router tree,
// and a small ProtectedRoute helper that redirects unauthenticated users to the
// login page.
import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CreditsProvider } from './contexts/CreditsContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { TierProvider } from './contexts/TierContext';
import { CartProvider } from './contexts/CartContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { initSentry } from './lib/sentry';
import { initAnalytics } from './lib/analytics';

import App from './App';
import Layout from './components/Layout';

// Route-level code splitting: lazy-load all page components
const AuthPage = lazy(() => import('./pages/AuthPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const BetaAnnouncementPage = lazy(() => import('./pages/BetaAnnouncementPage'));
const GeneratorSelectionPage = lazy(() => import('./pages/GeneratorSelectionPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));

const AccessoryGeneratorPage = lazy(() => import('./pages/AccessoryGeneratorPage'));
const ClothingGeneratorPage = lazy(() => import('./pages/ClothingGeneratorPage'));
const PetGeneratorPage = lazy(() => import('./pages/PetGeneratorPage'));

const ResultPage = lazy(() => import('./pages/ResultPage'));
const MarketplacePage = lazy(() => import('./pages/MarketplacePage'));
const MarketplaceLikedPage = lazy(() => import('./pages/MarketplaceLikedPage'));
const ListingDetailPage = lazy(() => import('./pages/ListingDetailPage'));
const JobsPage = lazy(() => import('./pages/JobsPage'));
const MyAssetsPage = lazy(() => import('./pages/MyAssetsPage'));
const UploadAssetPage = lazy(() => import('./pages/UploadAssetPage'));
const BuyCreditsPage = lazy(() => import('./pages/BuyCreditsPage'));
const RedeemProgramPage = lazy(() => import('./pages/RedeemProgramPage'));
const BillingPage = lazy(() => import('./pages/BillingPage'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminTiersPage = lazy(() => import('./pages/AdminTiersPage'));
const SupabaseTestPage = lazy(() => import('./pages/SupabaseTestPage'));
const CreatorProfilePage = lazy(() => import('./pages/CreatorProfilePage'));
const ProfileEditPage = lazy(() => import('./pages/ProfileEditPage'));
const ProfileSettingsPage = lazy(() => import('./pages/ProfileSettingsPage'));
const CreatorDirectoryPage = lazy(() => import('./pages/CreatorDirectoryPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const SubscriptionCheckoutPage = lazy(() => import('./pages/SubscriptionCheckoutPage'));
const CheckoutSuccessPage = lazy(() => import('./pages/CheckoutSuccessPage'));
const CheckoutCancelPage = lazy(() => import('./pages/CheckoutCancelPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const AnalyticsDashboard = lazy(() => import('./pages/AnalyticsDashboard'));
const WalletPage = lazy(() => import('./pages/WalletPage').then(m => ({ default: m.WalletPage })));
const StripeConnectReturnPage = lazy(() => import('./pages/StripeConnectReturnPage'));
const StripeConnectRefreshPage = lazy(() => import('./pages/StripeConnectRefreshPage'));
// DebugUserPage removed — privilege escalation vulnerability (any user could make themselves admin)
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const ViewerPage = lazy(() => import('./pages/ViewerPage'));
const ViewerResolverPage = lazy(() => import('./pages/ViewerResolverPage'));

import './index.css';

// Initialize Sentry and analytics before rendering
initSentry();
initAnalytics();

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  // The auth provider exposes both loading state and the current user; while the
  // session is still being resolved we render nothing to avoid flashing the
  // login page. If no user is present, redirect to signup
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) return null;
  
  if (!user) {
    // Redirect to AuthPage signup with return path
    // Tier selection will be handled by AuthPage based on URL params
    return <Navigate to="/auth?mode=signup&tier=free" state={{ from: location.pathname }} replace />;
  }
  
  // Email verification is checked at the generation level, not here
  // This allows users to sign up, checkout, and browse without friction
  
  return <>{children}</>;
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
    <AuthProvider>
      <TierProvider>
        <CartProvider>
          <CreditsProvider>
            <SubscriptionProvider>
              <BrowserRouter>
            <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 animate-spin rounded-full border-4 border-orange-500 border-t-transparent" /></div>}>
            <Routes>
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            {/* debug-user route removed — security vulnerability */}
            {/* DEPRECATED: /signup redirects to AuthPage - remove after migration */}
            <Route path="/signup" element={<Navigate to="/auth?mode=signup" replace />} />
            <Route path="/checkout" element={<SubscriptionCheckoutPage />} />

            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/tiers"
              element={
                <ProtectedRoute>
                  <AdminTiersPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/*"
              element={
                <Layout>
                  <Routes>
                    <Route path="/" element={<App />} />
                    <Route path="beta-announcement" element={<BetaAnnouncementPage />} />
                    <Route path="/pricing" element={<PricingPage />} />
                    <Route
                      path="/dashboard"
                      element={
                        <ProtectedRoute>
                          <DashboardPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/select-generator" element={<GeneratorSelectionPage />} />

                    <Route
                      path="/generate/accessory"
                      element={
                        <ProtectedRoute>
                          <AccessoryGeneratorPage />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/generate/clothing"
                      element={
                        <ProtectedRoute>
                          <ClothingGeneratorPage />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/generate/pet"
                      element={
                        <ProtectedRoute>
                          <PetGeneratorPage />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/result/:id"
                      element={
                        <ProtectedRoute>
                          <ResultPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/assets/:id"
                      element={
                        <ProtectedRoute>
                          <ResultPage />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/my-assets"
                      element={
                        <ProtectedRoute>
                          <MyAssetsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/assets"
                      element={
                        <ProtectedRoute>
                          <MyAssetsPage />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/upload"
                      element={
                        <ProtectedRoute>
                          <UploadAssetPage />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/marketplace"
                      element={<MarketplacePage />}
                    />
                    <Route
                      path="/listings/:id"
                      element={<ListingDetailPage />}
                    />
                    <Route
                      path="/marketplace/liked"
                      element={<MarketplaceLikedPage />}
                    />
                    <Route
                      path="/cart"
                      element={<CartPage />}
                    />
                    <Route
                      path="/jobs"
                      element={
                        <ProtectedRoute>
                          <JobsPage />
                        </ProtectedRoute>
                      }
                    />


                    <Route path="/viewer" element={<ViewerResolverPage />} />
                    <Route
                      path="/viewer/full"
                      element={
                        <ProtectedRoute>
                          <ViewerPage />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/checkout/:listingId"
                      element={
                        <ProtectedRoute>
                          <CheckoutPage />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/checkout-success"
                      element={
                        <ProtectedRoute>
                          <CheckoutSuccessPage />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/checkout-cancel"
                      element={<CheckoutCancelPage />}
                    />

                    <Route
                      path="/credits"
                      element={
                        <ProtectedRoute>
                          <BuyCreditsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/redeem"
                      element={
                        <ProtectedRoute>
                          <RedeemProgramPage />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/billing"
                      element={
                        <ProtectedRoute>
                          <BillingPage />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/analytics"
                      element={
                        <ProtectedRoute>
                          <AnalyticsDashboard />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/wallet"
                      element={
                        <ProtectedRoute>
                          <WalletPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/wallet/stripe-return"
                      element={
                        <ProtectedRoute>
                          <StripeConnectReturnPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/wallet/stripe-refresh"
                      element={
                        <ProtectedRoute>
                          <StripeConnectRefreshPage />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/profile"
                      element={
                        <ProtectedRoute>
                          <CreatorProfilePage />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/profile/:creatorId"
                      element={<CreatorProfilePage />}
                    />

                    <Route
                      path="/profile/edit"
                      element={
                        <ProtectedRoute>
                          <ProfileEditPage />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/account/profile"
                      element={
                        <ProtectedRoute>
                          <ProfileSettingsPage />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/creators"
                      element={<CreatorDirectoryPage />}
                    />

                    <Route path="/supabase-test" element={<SupabaseTestPage />} />

                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </Layout>
              }
            />

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
            </Suspense>
          </BrowserRouter>
        </SubscriptionProvider>
      </CreditsProvider>
      </CartProvider>
      </TierProvider>
    </AuthProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
